import React, { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaChartBar, FaExclamationTriangle } from 'react-icons/fa';
import { Button, getDefaultDateRange } from '../UI';
import GraphCard from '../../Routes/Visualization/GraphCard';
import useGraphMetrics from '../../Routes/Visualization/graphData';
import useGraphPreferences from '../../Routes/Visualization/graphPreferences';
import { buildCards, sourcesFor } from '../../Routes/Visualization/graphCards';
import { useAllowedSections } from '../../Routes/customHooks';

const prettyDate = (value) =>
  new Date(value).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });

/**
 * The graphs this clinic ticked "Show on dashboard" for, with data till date:
 * no start date (earliest record) up to today. It never follows the Graphs page
 * filter, and fetches only the metrics behind the ticked cards.
 */
const SelectedGraphs = ({ selected, onRemove, retainOnly, saving }) => {
  // Today is fixed for the life of the page; a new day needs a reload anyway.
  const range = useMemo(() => ({ startDate: '', endDate: getDefaultDateRange().endDate }), []);
  const sources = useMemo(() => [...new Set(selected.flatMap(sourcesFor))], [selected]);
  const { metrics, failed, loading, error, status } = useGraphMetrics(range, sources);

  const cards = useMemo(() => {
    const built = buildCards([], metrics);
    return selected.map((id) => built.find((card) => card.id === id)).filter(Boolean);
  }, [metrics, selected]);

  // A metric the server reports as unknown was removed or renamed: drop it from
  // the saved selection rather than leaving a dead tick behind.
  useEffect(() => {
    if (loading) return;
    const gone = new Set(
      Object.keys(failed || {}).filter((key) => failed[key] === 'Unknown metric'),
    );
    if (!gone.size) return;
    retainOnly((id) => !sourcesFor(id).some((key) => gone.has(key)));
  }, [loading, failed, retainOnly]);

  if (status === 401 || status === 403) return null;

  // Ticked ids the server no longer builds (metric renamed or removed). They are
  // listed rather than silently hidden, so the clinic can untick them here.
  const missing = loading ? [] : selected.filter((id) => !cards.some((card) => card.id === id));
  const note = `Till date (${prettyDate(range.endDate)})`;

  return (
    <>
      {error && (
        <div className="flex items-center gap-2 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
          <FaExclamationTriangle className="h-3.5 w-3.5 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {loading && !cards.length ? (
        <p className="rounded-2xl border border-slate-200/80 bg-white px-3 py-10 text-center text-xs text-slate-400">
          Loading graphs…
        </p>
      ) : (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 xl:grid-cols-3">
          {cards.map((card) => (
            <div key={card.id} className={card.span === 2 ? 'lg:col-span-2 xl:col-span-2' : undefined}>
              <GraphCard
                card={card}
                batch={metrics}
                failed={failed}
                loading={loading}
                showRangeFilter={false}
                note={note}
                onDashboard
                onToggleDashboard={(checked) => onRemove(card.id, checked)}
                dashboardDisabled={saving}
              />
            </div>
          ))}
        </div>
      )}

      {missing.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
          <FaExclamationTriangle className="h-3.5 w-3.5 shrink-0" />
          <span>
            {missing.length} selected graph{missing.length > 1 ? 's are' : ' is'} not available:
          </span>
          {missing.map((id) => (
            <button
              key={id}
              type="button"
              onClick={() => onRemove(id, false)}
              className="rounded-md border border-amber-300 bg-white px-2 py-0.5 font-medium hover:bg-amber-100"
              title="Remove from dashboard"
            >
              {failed?.[id] ? `${id} (${failed[id]})` : id} ✕
            </button>
          ))}
        </div>
      )}
    </>
  );
};

const DashboardGraphsSection = () => {
  const navigate = useNavigate();
  const { selected, loading, saving, error, status, setOnDashboard, retainOnly } = useGraphPreferences();

  if (status === 401 || status === 403) return null;

  return (
    <section className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-base font-bold text-slate-800 tracking-tight">Dashboard Graphs</h2>
          <p className="text-xs text-slate-500 mt-0.5">
            Graphs ticked “Show on dashboard” on the Graphs page, with data till date
          </p>
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={() => navigate('/graphs')}
          icon={<FaChartBar className="w-3.5 h-3.5" />}
        >
          Choose Graphs
        </Button>
      </div>

      {error && (
        <div className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
          <FaExclamationTriangle className="h-3.5 w-3.5 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {loading ? (
        <p className="rounded-2xl border border-slate-200/80 bg-white px-3 py-10 text-center text-xs text-slate-400">
          Loading graphs…
        </p>
      ) : selected.length ? (
        <SelectedGraphs selected={selected} onRemove={setOnDashboard} retainOnly={retainOnly} saving={saving} />
      ) : (
        <div className="rounded-2xl border border-dashed border-slate-200 bg-white/60 px-4 py-8 text-center">
          <p className="text-sm font-medium text-slate-600">No graph is on the dashboard yet</p>
          <p className="mt-1 text-xs text-slate-400">
            Open Graphs and tick “Show on dashboard” on any graph to show it here.
          </p>
        </div>
      )}
    </section>
  );
};

export default function DashboardGraphs() {
  const allowedSections = useAllowedSections();

  if (!Array.isArray(allowedSections) || !allowedSections.includes('graphs')) return null;

  return <DashboardGraphsSection />;
}
